/**
 * A17 (décision PO du 2026-09-16) — **la langue CHOISIE par la personne, dans ce
 * navigateur** : une fois choisie, les liens des e-mails de l'espace ne la changent
 * plus ; la langue de la fiche (`?lang=`) ne sert qu'à qui n'a rien choisi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ « LANGUE EN CACHE » N'EST PAS « LANGUE CHOISIE »
 * ═══════════════════════════════════════════════════════════════════════════
 * i18next remet en cache (`localStorage.i18nextLng`) TOUTE langue appliquée — celle
 * qu'il détecte dans `?lang=` au chargement (`changeLanguage` → `cacheUserLanguage`),
 * celle que la connexion du back-office applique depuis la préférence du compte
 * (`AuthContext`), celle qu'une page publique relit dans son lien. Cette clé ne dit
 * donc pas QUI a décidé. La clé ci-dessous, si : seul un GESTE l'écrit — le sélecteur
 * de langue (`LanguageSwitcher`), et seulement sur une adresse de l'espace (PO, 2026-09-17) —,
 * et la détection ne SAIT pas l'écrire (`caches` ne liste pas le détecteur de `i18n.ts`).
 *
 * ⛔ Aucune dépendance ici, pas même i18next : le sélecteur est monté par des tests qui
 * simulent `react-i18next`, où importer `@/i18n` lèverait (`initReactI18next` absent).
 *
 * Gardien : `components/portal/__tests__/laLangueChoisieGagneSurLeLienDeLEmail.test.tsx`.
 */

const CLE = 'argalis.langueChoisie'

/** Un code de langue tel que le sélecteur les écrit ; une valeur abîmée n'est pas un choix. */
const CODE = /^[a-z]{2}$/

/** La langue choisie par un geste dans ce navigateur, ou `null` si personne n'a choisi. */
export function langueChoisie(): string | null {
    try {
        const valeur = window.localStorage.getItem(CLE)
        return valeur && CODE.test(valeur) ? valeur : null
    } catch {
        // Stockage refusé (navigation privée, politique du navigateur) : aucun choix connu.
        return null
    }
}

/** À n'appeler que depuis un GESTE de la personne — jamais depuis une détection ou un lien. */
export function retenirLaLangueChoisie(code: string): void {
    try {
        window.localStorage.setItem(CLE, code)
    } catch {
        // Stockage refusé : le choix vaut pour la page ouverte, pas au-delà.
    }
}

/**
 * Les adresses de l'espace — le SEUL périmètre où le choix passe avant le lien.
 *
 * ⛔ Hors de l'espace, la détection reste celle d'avant, à l'identique : le back-office
 * applique ensuite la préférence du COMPTE collaborateur (`AuthContext`) ; un choix lu
 * avant elle, dès qu'il en diffère, peindrait chaque chargement dans une langue puis
 * dans l'autre. Les pages de l'espace, elles, ne lisent aucune préférence de compte
 * (`/espace` est dans les `PUBLIC_PATHS` d'`AuthContext` : `checkAuth` n'y part pas).
 */
export function estUneAdresseDeLEspace(chemin: string): boolean {
    return chemin === '/espace' || chemin.startsWith('/espace/')
}
